import {
  Component,
  ChangeDetectorRef,
  OnInit,
  AfterViewInit,
  Input,
  Injectable,
  ElementRef,
  ViewChild,
  DestroyRef,
  inject,
  LOCALE_ID,
  Inject,
  TemplateRef,
  ChangeDetectionStrategy,
} from "@angular/core";
import { RecordData, QuestionnaireAnswer } from "src/app/types";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MatTableDataSource } from "@angular/material/table";
import { MatChipSelectionChange } from "@angular/material/chips";
import { ActivatedRoute } from "@angular/router";
import { Subject } from "rxjs/internal/Subject";
import { GostUtils } from "./gost-utils";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { GostDigestComponent, MY_FORMATS } from "./gost-digest.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { GOST_I18N, getWeekdaysArray } from "./gost.i18n";
import { DetailsHeaderComponent } from "../../measured-data-details/components/shared/details-header/details-header.component";
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from "@angular/material/core";
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from "@angular/material-moment-adapter";

export interface PeriodicElement {
  type: string;
  score: string;
  value: string;
}

type GostOverviewWstRow = {
  stage: number;
  amount: number | null;
  result: string;
};

type GostOverviewWstAttemptRow = {
  amount: number | null;
  firstStatus: GostOverviewWstRoundStatus;
  secondStatus: GostOverviewWstRoundStatus | null;
  hasSecond: boolean;
};

type GostOverviewWstSummary = {
  amountText: string;
  statusText: string;
};

type GostOverviewWstRoundStatus = {
  result: string;
  measured: boolean;
  swallowPresent: boolean;
  coughPresent: boolean;
  abnormalCount: number;
};

type GostOverviewRiskItem = {
  section: "GC" | "OC";
  itemLabel: string;
  optionLabel: string;
  severity: "bad" | "acceptable";
};

type GostReportQueryParams = {
  recordId: string;
};

@Injectable()
class GostInfosDateAdapter extends MomentDateAdapter {
  getDayOfWeekNames(style: "long" | "short" | "narrow") {
    return getWeekdaysArray();
  }
  override getDateNames(): string[] {
    return Array.from({ length: 31 }, (_, i) => String(i + 1));
  }
  getFirstDayOfWeek(): number {
    return 0;
  }
}

@Component({
  imports: [...SHARED_IMPORTS, GostDigestComponent, DetailsHeaderComponent],
  selector: "gost-infos-card",
  templateUrl: "./gost-infos.component.html",
  styleUrls: ["./recording.component.css"],
  providers: [
    GostUtils,
    {
      provide: DateAdapter,
      useClass: GostInfosDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MY_FORMATS,
    },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: (localeId: string) =>
        (localeId || "").toLowerCase().startsWith("en") ? "en-US" : "ja-JP",
      deps: [LOCALE_ID],
    },
  ],
  host: {
    "(window:resize)": "onResize()",
    "(document:click)": "onDocumentClick($event)",
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GostInfosComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  protected readonly i18n = GOST_I18N;

  dataSource: PeriodicElement[];

  @Input() recordData: RecordData;
  @Input() recordDataUpdated: Subject<any>;
  @Input() afterScTemplate: TemplateRef<unknown> | null = null;

  isHandset = false;
  readonly clearInputsTooltip = $localize`:@@home.gostDigest.button.clearInputs:入力をクリア`;
  readonly saveTooltip = $localize`:@@home.gostDigest.button.save:保存`;

  ocTableDef: GcOcTebleDef;
  gcTableDef: GcOcTebleDef;
  scWoThicknessTableDef: ScTableDef;
  scWithThicknessTableDef: ScTableDef;
  // 3種類すべての GOST データが揃っているかどうかで描画可否を制御する
  hasGostData = false;

  sections = [
    { title: GOST_I18N.section.gc, desc: GOST_I18N.section.gcDesc, icon: "ecg_heart", iconFontSet: "material-symbols-outlined" },
    {
      title: GOST_I18N.section.oc,
      desc: GOST_I18N.section.ocDesc,
      icon: "assist_walker",
      iconFontSet: "material-symbols-outlined",
    },
  ];

  mobileSummaries: {
    key: "sc" | "gc" | "oc" | "fils";
    title: string;
    score: number | null;
    level: number | null;
    abnormalCount: number;
    recommendation?: string | null;
  }[] = [];

  overviewWstRows: GostOverviewWstRow[] = [];
  overviewWstAttemptRows: GostOverviewWstAttemptRow[] = [];
  overviewWstAmountText = "-";
  overviewWstStatusText = $localize`:@@home.gostInfos.status.notMeasured:未測定`;
  overviewRiskItems: GostOverviewRiskItem[] = [];
  overviewQuestionnairePositiveCount = 0;
  overviewQuestionnaireTotalCount = 0;

  mobileExpanded: Record<"sc" | "gc" | "oc", boolean> = {
    sc: false,
    gc: false,
    oc: false,
  };

  scInputMode: "without" | "with" = "without";
  scInputDisplayMode: "card" | "table" = "card";
  scSelectedTrialIndex: Record<"without" | "with", number> = {
    without: 0,
    with: 0,
  };

  /** デスクトップインラインテーブルで異常展開中の試行列 */
  scExpandedAbnormalCol: string | null = null;
  /** モバイルで異常セクション展開中かどうか */
  scMobileAbnormalExpanded = true;

  readonly scPrimaryCodes = ["swallowResult", "coughResult"];
  readonly scAbnormalCodes = [
    "swallowDelay",
    "abnormalSound",
    "multiSwallows",
    "throatClear",
    "otherAbbration",
  ];
  readonly scEditableCodes = [...this.scPrimaryCodes, ...this.scAbnormalCodes];

  @ViewChild("scSectionRef") scSectionRef: ElementRef<HTMLElement>;
  @ViewChild("gcSectionRef") gcSectionRef: ElementRef<HTMLElement>;
  @ViewChild("ocSectionRef") ocSectionRef: ElementRef<HTMLElement>;

  @ViewChild("gost_digest_card") gostDigestComponent: GostDigestComponent;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    public gostUtils: GostUtils,
    @Inject(LOCALE_ID) public locale: string,
  ) {}

  ngOnInit() {

    this.updateIsHandset();
    if (this.recordData) {
      this.initializeTableDefs();
    }

    this.recordDataUpdated.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((newRecordData) => {
      this.recordData = newRecordData;
      this.initializeTableDefs();
      this.cdr.markForCheck();
    });

    // this.route.params.subscribe(params => {
    // 	// TableDefのインスタンスを作成する
    // 	this.initializeTableDefs();
    // });
  }

  ngAfterViewInit() {}

  ngOnChange() {
  }

  private initializeTableDefs() {

    const data = this.recordData?.data;
    const hasOcData = !!data?.gostOCData;
    const hasGcData = !!data?.gostGCData;
    const hasScData = !!data?.gostSCData;
    this.hasGostData = hasOcData && hasGcData && hasScData;
    // RSST など GOST データを持たないケースではテーブルを生成しない
    if (!this.hasGostData) {
      this.ocTableDef = null;
      this.gcTableDef = null;
      this.scWoThicknessTableDef = null;
      this.scWithThicknessTableDef = null;
      this.updateMobileSummaries();
      this.updateOverviewRows();
      return;
    }

    const oc = this.i18n.oc;
    this.ocTableDef = new GcOcTebleDef(
      this,
      data.gostOCData,
      ["status"],
      [
        new TableRow("OC_TEETH", oc.teeth.label, oc.teeth.options as unknown as string[], oc.teeth.shortLabel),
        new TableRow("OC_DENTURE", oc.denture.label, oc.denture.options as unknown as string[], oc.denture.shortLabel),
        new TableRow("OC_ORAL_HYGIENE", oc.oralHygiene.label, oc.oralHygiene.options as unknown as string[], oc.oralHygiene.shortLabel),
        new TableRow("OC_ARTICULATION_LEVEL", oc.articulationLevel.label, oc.articulationLevel.options as unknown as string[], oc.articulationLevel.shortLabel),
        new TableRow("OC_RIGHT_ARM", oc.rightArm.label, oc.rightArm.options as unknown as string[], oc.rightArm.shortLabel),
        new TableRow("OC_LEFT_ARM", oc.leftArm.label, oc.leftArm.options as unknown as string[], oc.leftArm.shortLabel),
        new TableRow("OC_DISABILITY_LEVEL", oc.disabilityLevel.label, oc.disabilityLevel.options as unknown as string[], oc.disabilityLevel.shortLabel),
        new TableRow("OC_COGNITIVE_LEVEL", oc.cognitiveLevel.label, oc.cognitiveLevel.options as unknown as string[], oc.cognitiveLevel.shortLabel),
      ],
    );

    const gc = this.i18n.gc;
    this.gcTableDef = new GcOcTebleDef(
      this,
      data.gostGCData,
      ["status"],
      [
        new TableRow("GC_CONSCIOUSNESS_LEVEL", gc.consciousnessLevel.label, gc.consciousnessLevel.options as unknown as string[], gc.consciousnessLevel.shortLabel),
        new TableRow("GC_TEMPERATURE", gc.temperature.label, gc.temperature.options as unknown as string[], gc.temperature.shortLabel),
        new TableRow("GC_PULSE_LEVEL", gc.pulseLevel.label, gc.pulseLevel.options as unknown as string[], gc.pulseLevel.shortLabel),
        new TableRow("GC_SPO2", gc.spo2.label, gc.spo2.options as unknown as string[], gc.spo2.shortLabel),
        new TableRow("GC_BREATH_LEVEL", gc.breathLevel.label, gc.breathLevel.options as unknown as string[], gc.breathLevel.shortLabel),
        new TableRow("GC_ON_OXYGEN", gc.onOxygen.label, gc.onOxygen.options as unknown as string[], gc.onOxygen.shortLabel),
        new TableRow("GC_SPUTUM", gc.sputum.label, gc.sputum.options as unknown as string[], gc.sputum.shortLabel),
      ],
    );

    const sc = this.i18n.sc;
    this.scWoThicknessTableDef = new ScTableDef(
      this,
      data.gostSCData,
      [
        "1ml 1回目",
        "1ml 2回目",
        "3ml 1回目",
        "3ml 2回目",
        "5ml 1回目",
        "5ml 2回目",
        "30ml",
        "100ml",
      ],
      this.gostUtils.WithOrWithoutThickness.withoutThickness,
      [
        new TableRow("swallowResult", sc.swallowResult.label, ["-", "-", "-", "-", "-", "-", "-", "-"], sc.swallowResult.shortLabel),
        new TableRow("coughResult", sc.coughResult.label, ["-", "-", "-", "-", "-", "-", "-", "-"], sc.coughResult.shortLabel),
        new TableRow("otherAbnormality", sc.otherAbnormality.label, ["-", "-", "-", "-", "-", "-", "-", "-"], sc.otherAbnormality.shortLabel),
        new TableRow("swallowDelay", sc.swallowDelay.label, ["-", "-", "-", "-", "-", "-", "-", "-"], sc.swallowDelay.shortLabel),
        new TableRow("abnormalSound", sc.abnormalSound.label, ["-", "-", "-", "-", "-", "-", "-", "-"], sc.abnormalSound.shortLabel),
        new TableRow("multiSwallows", sc.multiSwallows.label, ["-", "-", "-", "-", "-", "-", "-", "-"], sc.multiSwallows.shortLabel),
        new TableRow("throatClear", sc.throatClear.label, ["-", "-", "-", "-", "-", "-", "-", "-"], sc.throatClear.shortLabel),
        new TableRow("otherAbbration", sc.otherAbbration.label, ["-", "-", "-", "-", "-", "-", "-", "-"], sc.otherAbbration.shortLabel),
        new TableRow("testResult", sc.testResult.label, ["-", "-", "-", "-", "-", "-", "-", "-"], sc.testResult.shortLabel),
      ],
    );

    this.scWithThicknessTableDef = new ScTableDef(
      this,
      data.gostSCData,
      ["1ml", "3ml", "5ml", "30ml"],
      this.gostUtils.WithOrWithoutThickness.withThickness,
      [
        new TableRow("swallowResult", sc.swallowResult.label, ["-", "-", "-", "-"], sc.swallowResult.shortLabel),
        new TableRow("coughResult", sc.coughResult.label, ["-", "-", "-", "-"], sc.coughResult.shortLabel),
        new TableRow("otherAbnormality", sc.otherAbnormality.label, ["-", "-", "-", "-"], sc.otherAbnormality.shortLabel),
        new TableRow("swallowDelay", sc.swallowDelay.label, ["-", "-", "-", "-"], sc.swallowDelay.shortLabel),
        new TableRow("abnormalSound", sc.abnormalSound.label, ["-", "-", "-", "-"], sc.abnormalSound.shortLabel),
        new TableRow("multiSwallows", sc.multiSwallows.label, ["-", "-", "-", "-"], sc.multiSwallows.shortLabel),
        new TableRow("throatClear", sc.throatClear.label, ["-", "-", "-", "-"], sc.throatClear.shortLabel),
        new TableRow("otherAbbration", sc.otherAbbration.label, ["-", "-", "-", "-"], sc.otherAbbration.shortLabel),
        new TableRow("testResult", sc.testResult.label, ["-", "-", "-", "-"], sc.testResult.shortLabel),
      ],
    );

    // 入力UIは常に通常水（1/3/5mlは2回試行）から開始する
    this.scInputMode = "without";
    this.scSelectedTrialIndex = { without: 0, with: 0 };

    this.updateMobileSummaries();
    this.updateOverviewRows();
  }

  isInputMode() {
    return this.recordData.id == this.gostUtils.newGostId;
  }

  onResize() {
    this.updateIsHandset();
    this.cdr.markForCheck();
  }

  private updateIsHandset() {
    if (typeof window !== "undefined") {
      this.isHandset = window.innerWidth <= 768;
      if (!this.isHandset) {
        this.mobileExpanded = { sc: false, gc: false, oc: false };
      } else {
        // モバイルでは表モードを提供しない
        this.scInputDisplayMode = "card";
      }
    }
  }

  toggleMobileSection(key: "sc" | "gc" | "oc") {
    const nextState = !this.mobileExpanded[key];
    this.mobileExpanded = { sc: false, gc: false, oc: false };
    this.mobileExpanded[key] = nextState;
  }

  expandMobileSection(key: "sc" | "gc" | "oc") {
    this.mobileExpanded = { sc: false, gc: false, oc: false };
    this.mobileExpanded[key] = true;
    requestAnimationFrame(() => this.scrollToSection(key));
  }

  onSummaryCardClick(key: "sc" | "gc" | "oc" | "fils") {
    if (key === "fils") {
      return;
    }
    if (this.isHandset) {
      this.toggleMobileSection(key);
      // Wait for section to render, then scroll.
      requestAnimationFrame(() =>
        this.scrollToSection(key as "sc" | "gc" | "oc"),
      );
    } else {
      this.scrollToSection(key);
    }
  }

  scrollToSection(key: "sc" | "gc" | "oc") {
    const map = {
      sc: this.scSectionRef,
      gc: this.gcSectionRef,
      oc: this.ocSectionRef,
    };

    const target = map[key]?.nativeElement;
    if (target) {
      target.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
      return;
    }

    // If the section isn't rendered yet (mobile expand), try once more on next tick.
    setTimeout(() => {
      const retryTarget = map[key]?.nativeElement;
      retryTarget?.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }, 0);
  }

  updateMobileSummaries() {
    if (!this.hasGostData || !this.recordData?.data) {
      this.mobileSummaries = [];
      return;
    }

    const scData = this.recordData.data.gostSCData;
    const gcData = this.recordData.data.gostGCData;
    const ocData = this.recordData.data.gostOCData;

    this.mobileSummaries = [
      {
        key: "fils",
        title: this.i18n.section.fils,
        score: this.recordData.data.filsGrade ?? null,
        level: null,
        abnormalCount: 0,
        recommendation:
          this.recordData.data.gostRecommendation ??
          this.recordData.data.filsRecommendation ??
          null,
      },
      {
        key: "sc",
        title: this.i18n.section.sc,
        score: scData?.totalScore ?? null,
        level: scData?.level ?? null,
        abnormalCount: this.countAbnormalSc(scData),
      },
      {
        key: "gc",
        title: this.i18n.section.gc,
        score: gcData?.totalScore ?? null,
        level: gcData?.level ?? null,
        abnormalCount: this.countAbnormalGcOc(gcData),
      },
      {
        key: "oc",
        title: this.i18n.section.oc,
        score: ocData?.totalScore ?? null,
        level: ocData?.level ?? null,
        abnormalCount: this.countAbnormalGcOc(ocData),
      },
    ];
  }

  getOverviewFilsLevelText(): string {
    const level = this.recordData?.data?.filsGrade;
    return level == null ? "-" : `Lv.${level}`;
  }

  getOverviewRecommendationText(): string {
    return this.getTopRankedRecommendationText();
  }

  getHeaderPatientName(): string {
    return this.recordData?.data?.userName ?? "";
  }

  getHeaderMeasureDate(): string {
    const d = this.recordData?.data;
    const createdAt = d?.createdAt ?? d?.recordedAt ?? "";
    const offsetMatch = createdAt.match(/([+-])(\d{2}):(\d{2})$/);
    const hasNonZeroOffset =
      offsetMatch &&
      !(offsetMatch[1] === "+" && offsetMatch[2] === "00" && offsetMatch[3] === "00");

    if (hasNonZeroOffset && d?.createdAtLocationStr) {
      const dateStr = d.createdAtLocationStr.replace(/[+-]\d{2}:\d{2}$/, "").trim();
      if (!dateStr) return "";
      const h = parseInt(offsetMatch[2], 10);
      const m = parseInt(offsetMatch[3], 10);
      const tzLabel = m > 0
        ? `UTC${offsetMatch[1]}${h}:${String(m).padStart(2, "0")}`
        : `UTC${offsetMatch[1]}${h}`;
      return `${dateStr} (${tzLabel})`;
    }

    const raw = d?.createdAtLocalStr ?? d?.recordedAt ?? createdAt;
    const dateStr = raw.replace(/[+-]\d{2}:\d{2}$/, "").trim();
    if (!dateStr) return "";
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return tz ? `${dateStr} (${tz})` : dateStr;
    } catch {
      return dateStr;
    }
  }

  getHeaderMeasurer(): string {
    const d = this.recordData?.data;
    if (d?.recorderName) {
      return `計測者: ${d.recorderName}`;
    }
    const m = this.recordData?.meta?.measurer;
    if (m?.lastName || m?.firstName) {
      return `計測者: ${(m.lastName ?? "") + (m.firstName ?? "")}`;
    }
    return "";
  }

  getGostReportLink(): string {
    const sectionId = this.recordData?.meta?.location?.sectionId ?? "";
    const userId = this.recordData?.meta?.user?.userId ?? "";
    if (!sectionId || !userId) {
      return "";
    }
    return `/home/${sectionId}/users/${userId}/gost/overview`;
  }

  getGostReportQueryParams(): GostReportQueryParams | null {
    const recordId = this.recordData?.id ?? "";
    if (!recordId) {
      return null;
    }
    return { recordId };
  }

  hasOverviewWstCaution(status: GostOverviewWstRoundStatus): boolean {
    if (!status?.measured) {
      return false;
    }
    return status.result !== this.gostUtils.testResultDisp.good;
  }

  getOverviewRiskClass(item: GostOverviewRiskItem): string {
    return item.severity === "bad"
      ? "gost-result-overview__risk-pill--bad"
      : "gost-result-overview__risk-pill--acceptable";
  }

  private updateOverviewRows() {
    if (!this.hasGostData || !this.recordData?.data) {
      this.overviewWstRows = [];
      this.overviewWstAttemptRows = [];
      this.overviewWstAmountText = "-";
      this.overviewWstStatusText = $localize`:@@home.gostInfos.status.notMeasured:未測定`;
      this.overviewRiskItems = [];
      this.overviewQuestionnairePositiveCount = 0;
      this.overviewQuestionnaireTotalCount = 0;
      return;
    }

    const data = this.recordData.data;
    this.overviewWstRows = this.buildOverviewWstRows(data?.gostSCData);
    this.overviewWstAttemptRows = this.buildOverviewWstAttemptRows(data?.gostSCData);
    const wstSummary = this.buildOverviewWstSummary(data?.gostSCData);
    this.overviewWstAmountText = wstSummary.amountText;
    this.overviewWstStatusText = wstSummary.statusText;
    this.overviewRiskItems = this.buildOverviewRiskItems();
    this.overviewQuestionnairePositiveCount =
      this.countAbnormalGcOc(data?.gostGCData) +
      this.countAbnormalGcOc(data?.gostOCData);
    this.overviewQuestionnaireTotalCount =
      (data?.gostGCData?.metrics?.length ?? 0) +
      (data?.gostOCData?.metrics?.length ?? 0);
  }

  private buildOverviewWstRows(scData: any): GostOverviewWstRow[] {
    const rows: GostOverviewWstRow[] = [];
    const steps = scData?.withoutThicknessSteps ?? [];

    for (const step of steps) {
      const measuredRound = (step?.rounds ?? []).find(
        (round: any) => round?.isMeasured,
      );
      if (!measuredRound) {
        continue;
      }

      rows.push({
        stage: rows.length + 1,
        amount:
          typeof step?.amount === "number"
            ? step.amount
            : Number.isFinite(Number(step?.amount))
              ? Number(step?.amount)
              : null,
        result:
          this.gostUtils.evaluateScRound(measuredRound)?.testResult ??
          this.gostUtils.testResultDisp.notMeasured,
      });

      if (rows.length >= 3) {
        return rows;
      }
    }

    if (rows.length > 0) {
      return rows;
    }

    for (const step of steps.slice(0, 3)) {
      rows.push({
        stage: rows.length + 1,
        amount:
          typeof step?.amount === "number"
            ? step.amount
            : Number.isFinite(Number(step?.amount))
              ? Number(step?.amount)
              : null,
        result: this.gostUtils.testResultDisp.notMeasured,
      });
    }

    return rows;
  }

  private buildOverviewWstAttemptRows(scData: any): GostOverviewWstAttemptRow[] {
    const steps = scData?.withoutThicknessSteps ?? [];
    const rows: GostOverviewWstAttemptRow[] = [];

    for (const step of steps) {
      const rawAmount = Number(step?.amount);
      const amount = Number.isFinite(rawAmount) ? rawAmount : null;
      const rounds = step?.rounds ?? [];

      const firstRound =
        rounds.find((round: any) => Number(round?.round) === 1) ??
        rounds[0] ??
        null;
      const secondRound =
        rounds.find((round: any) => Number(round?.round) === 2) ?? null;

      rows.push({
        amount,
        firstStatus: this.getOverviewRoundStatus(firstRound),
        secondStatus: secondRound
          ? this.getOverviewRoundStatus(secondRound)
          : null,
        hasSecond: !!secondRound,
      });
    }

    return rows;
  }

  private buildOverviewWstSummary(scData: any): GostOverviewWstSummary {
    const steps = scData?.withoutThicknessSteps ?? [];
    const stepEvaluations: {
      amount: number | null;
      measuredCount: number;
      hasSwallow: boolean;
      hasAbnormal: boolean;
    }[] = [];
    let maxMeasuredAmount: number | null = null;
    let hasMeasured = false;

    for (const step of steps) {
      const rawAmount = Number(step?.amount);
      const amount = Number.isFinite(rawAmount) ? rawAmount : null;
      let measuredCount = 0;
      let hasSwallow = false;
      let hasAbnormal = false;

      for (const round of step?.rounds ?? []) {
        if (!round?.isMeasured) {
          continue;
        }
        hasMeasured = true;
        measuredCount++;

        if (amount != null) {
          maxMeasuredAmount =
            maxMeasuredAmount == null
              ? amount
              : Math.max(maxMeasuredAmount, amount);
        }

        const status = this.getOverviewRoundStatus(round);
        if (status.swallowPresent) {
          hasSwallow = true;
        }
        if (
          status.coughPresent ||
          status.abnormalCount > 0 ||
          status.result !== this.gostUtils.testResultDisp.good
        ) {
          hasAbnormal = true;
        }
      }

      if (measuredCount > 0) {
        stepEvaluations.push({
          amount,
          measuredCount,
          hasSwallow,
          hasAbnormal,
        });
      }
    }

    const amountText =
      maxMeasuredAmount != null ? `${maxMeasuredAmount} mL` : "-";

    if (!hasMeasured) {
      return { amountText, statusText: $localize`:@@home.gostInfos.status.notMeasured:未測定` };
    }

    const targetStep =
      maxMeasuredAmount != null
        ? stepEvaluations.find((step) => step.amount === maxMeasuredAmount) ??
          null
        : stepEvaluations[0] ?? null;

    if (!targetStep) {
      return { amountText, statusText: $localize`:@@home.gostInfos.status.indeterminate:判定不能` };
    }

    const statusParts: string[] = [
      targetStep.hasSwallow ? $localize`:@@home.gostInfos.status.swallowPresent:嚥下あり` : $localize`:@@home.gostInfos.status.swallowAbsent:嚥下なし`,
    ];
    if (targetStep.hasAbnormal) {
      statusParts.push($localize`:@@home.gostInfos.status.abnormalPresent:異常あり`);
    }

    return {
      amountText,
      statusText: statusParts.join("・"),
    };
  }

  private getOverviewRoundStatus(round: any): GostOverviewWstRoundStatus {
    if (!round?.isMeasured) {
      return {
        result: this.gostUtils.testResultDisp.notMeasured,
        measured: false,
        swallowPresent: false,
        coughPresent: false,
        abnormalCount: 0,
      };
    }
    const evaluated = this.gostUtils.evaluateScRound(round) ?? {};
    const abnormalCount = [
      evaluated.swallowDelay,
      evaluated.abnormalSound,
      evaluated.multiSwallows,
      evaluated.throatClear,
      evaluated.otherAbbration,
    ].filter((v) => v === "TP").length;

    return {
      result: evaluated.testResult ?? this.gostUtils.testResultDisp.notMeasured,
      measured: true,
      swallowPresent: evaluated.swallowResult === "TP",
      coughPresent: evaluated.coughResult === "TP",
      abnormalCount,
    };
  }

  private buildOverviewRiskItems(): GostOverviewRiskItem[] {
    const items: GostOverviewRiskItem[] = [];

    const collectItems = (tableDef: GcOcTebleDef, section: "GC" | "OC") => {
      if (!tableDef?.tableRows?.length) {
        return;
      }
      for (const row of tableDef.tableRows) {
        const score = tableDef.getScore(row.code);
        if (score == null || score >= 0) {
          continue;
        }
        items.push({
          section,
          itemLabel: row.label,
          optionLabel: tableDef.getSelectedOptionLabel(row.code),
          severity: score <= -3 ? "bad" : "acceptable",
        });
      }
    };

    collectItems(this.gcTableDef, "GC");
    collectItems(this.ocTableDef, "OC");

    return items;
  }

  private countAbnormalSc(scData: any): number {
    if (!scData) {
      return 0;
    }

    const withOrWithout = [
      ...(scData.withoutThicknessSteps ?? []),
      ...(scData.withThicknessSteps ?? []),
    ];

    let count = 0;
    for (const step of withOrWithout) {
      for (const round of step?.rounds ?? []) {
        if (!round?.isMeasured) {
          continue;
        }
        const result = this.gostUtils.evaluateScRound(round);
        if (
          result?.testResult &&
          result.testResult !== this.gostUtils.testResultDisp.good
        ) {
          count++;
        }
      }
    }

    return count;
  }

  private countAbnormalGcOc(data: any): number {
    if (!data?.metrics?.length) {
      return 0;
    }

    return data.metrics.filter((m) => m?.score != null && m.score < 0).length;
  }

  private getTopRankedRecommendationText(): string {
    const selectedFoods = this.recordData?.data?.PrimarilySelectedFoods?.selectedFoods;
    const noMatchLabel = this.i18n.common.noMatch;

    if (Array.isArray(selectedFoods) && selectedFoods.length) {
      const rankedCandidates = selectedFoods
        .map((food) => ({
          name: typeof food?.name === "string" ? food.name.trim() : "",
          rank: Number(food?.rank),
        }))
        .filter(
          (food) =>
            !!food.name &&
            food.name !== noMatchLabel &&
            Number.isFinite(food.rank),
        );

      if (rankedCandidates.length) {
        rankedCandidates.sort((a, b) => a.rank - b.rank);
        return rankedCandidates[0].name;
      }

      const namedFood = selectedFoods.find(
        (food) =>
          typeof food?.name === "string" &&
          food.name.trim() &&
          food.name !== noMatchLabel,
      );
      if (namedFood?.name) {
        return namedFood.name;
      }
    }

    return (
      this.recordData?.data?.gostRecommendation ??
      this.recordData?.data?.filsRecommendation ??
      "-"
    );
  }

  private resolveTimezoneName(isoString: string): string {
    if (!isoString) return "";
    const match = isoString.match(/([+-])(\d{2}):(\d{2})$/);
    if (!match) return "";
    const sign = match[1] === "+" ? 1 : -1;
    const offsetMin =
      sign * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10));
    const offsetToTz: Record<number, string> = {
      540: "Asia/Tokyo",
      480: "Asia/Shanghai",
      420: "Asia/Bangkok",
      330: "Asia/Kolkata",
      0: "UTC",
      [-300]: "America/New_York",
      [-360]: "America/Chicago",
      [-420]: "America/Denver",
      [-480]: "America/Los_Angeles",
      60: "Europe/Paris",
      120: "Europe/Helsinki",
    };
    return offsetToTz[offsetMin] ?? `UTC${match[1]}${match[2]}:${match[3]}`;
  }

  getActiveScTableDef(): ScTableDef {
    return this.scInputMode === "without"
      ? this.scWoThicknessTableDef
      : this.scWithThicknessTableDef;
  }

  getScInputModeLabel(): string {
    return this.scInputMode === "without" ? "通常水" : "トロミ";
  }

  getScTrialCount(): number {
    return this.getActiveScTableDef()?.dataColumnDefs?.length ?? 0;
  }

  setScInputMode(mode: "without" | "with") {
    this.scInputMode = mode;
    this.ensureScTrialIndexInRange();
  }

  setScInputDisplayMode(mode: "card" | "table") {
    this.scInputDisplayMode = mode;
  }

  private ensureScTrialIndexInRange() {
    const tableDef = this.getActiveScTableDef();
    if (!tableDef?.dataColumnDefs?.length) {
      this.scSelectedTrialIndex[this.scInputMode] = 0;
      return;
    }

    const maxIndex = tableDef.dataColumnDefs.length - 1;
    const current = this.scSelectedTrialIndex[this.scInputMode] ?? 0;
    this.scSelectedTrialIndex[this.scInputMode] = Math.min(
      Math.max(current, 0),
      maxIndex,
    );
  }

  getScActiveColumnDef(): string {
    const tableDef = this.getActiveScTableDef();
    if (!tableDef?.dataColumnDefs?.length) {
      return "";
    }

    this.ensureScTrialIndexInRange();
    return tableDef.dataColumnDefs[this.scSelectedTrialIndex[this.scInputMode]];
  }

  selectScTrialByIndex(index: number) {
    const tableDef = this.getActiveScTableDef();
    if (!tableDef?.dataColumnDefs?.length) {
      return;
    }

    const maxIndex = tableDef.dataColumnDefs.length - 1;
    const newIndex = Math.min(Math.max(index, 0), maxIndex);

    this.scSelectedTrialIndex[this.scInputMode] = newIndex;

    // 入力モード時はデスクトップ注意項目パネルを新しい列で自動展開
    if (this.isInputMode()) {
      this.scExpandedAbnormalCol = tableDef.dataColumnDefs[newIndex];
    }
  }

  getScActiveTrialIndex(): number {
    this.ensureScTrialIndexInRange();
    return this.scSelectedTrialIndex[this.scInputMode];
  }

  navigateScTrial(direction: -1 | 1) {
    this.selectScTrialByIndex(
      this.scSelectedTrialIndex[this.scInputMode] + direction,
    );
  }

  canNavigateScPrev(): boolean {
    return this.scSelectedTrialIndex[this.scInputMode] > 0;
  }

  canNavigateScNext(): boolean {
    const tableDef = this.getActiveScTableDef();
    if (!tableDef?.dataColumnDefs?.length) {
      return false;
    }
    return this.scSelectedTrialIndex[this.scInputMode] < tableDef.dataColumnDefs.length - 1;
  }

  getScTrialStatusText(columnDef: string): string {
    const tableDef = this.getActiveScTableDef();
    if (!tableDef || !columnDef) {
      return "-";
    }

    if (!this.isInputMode()) {
      return tableDef.isMeasured(columnDef) ? $localize`:@@home.gostInfos.scStatus.measured:測定済み` : $localize`:@@home.gostInfos.scStatus.notMeasured:未測定`;
    }

    const isActive = this.getScActiveColumnDef() === columnDef;
    if (isActive) {
      return $localize`:@@home.gostInfos.scStatus.inProgress:入力中`;
    }

    if (!tableDef.isMeasured(columnDef)) {
      return $localize`:@@home.gostInfos.scStatus.notEntered:未入力`;
    }
    return $localize`:@@home.gostInfos.scStatus.completed:完了`;
  }

  getScTrialStatusClass(columnDef: string): string {
    const tableDef = this.getActiveScTableDef();
    if (!tableDef || !columnDef) {
      return "is-pending";
    }

    if (!this.isInputMode()) {
      return tableDef.isMeasured(columnDef) ? "is-complete" : "is-pending";
    }

    const isActive = this.getScActiveColumnDef() === columnDef;
    if (isActive) {
      return "is-editing";
    }

    return tableDef.isMeasured(columnDef) ? "is-complete" : "is-pending";
  }

  onGcOcOptionClick(
    tableDef: GcOcTebleDef,
    rowIndex: number,
    colIndex: number,
  ) {
    if (!this.isInputMode()) {
      return;
    }
    tableDef.setScoreByRowIndex(rowIndex, colIndex);
  }

  onScPrimaryChoice(
    tableDef: ScTableDef,
    rowCode: string,
    selected: boolean,
  ) {
    if (!this.isInputMode()) {
      return;
    }
    this.setScCellPositive(tableDef, rowCode, this.getScActiveColumnDef(), selected);
  }

  onScAbnormalToggle(tableDef: ScTableDef, rowCode: string) {
    if (!this.isInputMode()) {
      return;
    }
    const columnDef = this.getScActiveColumnDef();
    this.setScCellPositive(
      tableDef,
      rowCode,
      columnDef,
      !this.isScCellPositive(tableDef, rowCode, columnDef),
    );
  }

  onScClearActiveTrial() {
    if (!this.isInputMode()) {
      return;
    }
    this.clearScActiveTrial();
  }

  onScGoToNextTrial() {
    if (!this.isInputMode()) {
      return;
    }
    this.goToNextUnfilledScTrial();
  }

  isScMeasuredOnActiveTrial(): boolean {
    const tableDef = this.getActiveScTableDef();
    const colDef = this.getScActiveColumnDef();
    if (!tableDef || !colDef) {
      return false;
    }
    return tableDef.isMeasured(colDef);
  }

  getScTrialStats(tableDef: ScTableDef): {
    total: number;
    measured: number;
    unmeasured: number;
    attention: number;
  } {
    if (!tableDef?.dataColumnDefs?.length) {
      return { total: 0, measured: 0, unmeasured: 0, attention: 0 };
    }

    const total = tableDef.dataColumnDefs.length;
    let measured = 0;
    let attention = 0;
    for (const col of tableDef.dataColumnDefs) {
      if (!tableDef.isMeasured(col)) {
        continue;
      }
      measured++;
      const result = this.getScTestResult(tableDef, col);
      if (result && result !== this.gostUtils.testResultDisp.good) {
        attention++;
      }
    }

    return {
      total,
      measured,
      unmeasured: total - measured,
      attention,
    };
  }

  getScTestResult(tableDef: ScTableDef, columnDef: string): string {
    if (!tableDef || !columnDef) {
      return "-";
    }
    const row = tableDef.tableRows.find((r) => r.code === "testResult");
    if (!row) {
      return "-";
    }
    const colIndex = tableDef.dataColumnDefs.findIndex((c) => c === columnDef);
    if (colIndex < 0) {
      return "-";
    }
    return row.cellValues[colIndex] ?? "-";
  }

  getScActiveTestResult(): string {
    return this.getScTestResult(this.getActiveScTableDef(), this.getScActiveColumnDef());
  }

  getScResultDisplay(testResult: string): string {
    if (testResult === this.gostUtils.testResultDisp.good) {
      return "○";
    }
    if (testResult === this.gostUtils.testResultDisp.acceptable) {
      return "△";
    }
    if (testResult === this.gostUtils.testResultDisp.bad) {
      return "×";
    }
    return "-";
  }

  getScTestResultClassByValue(testResult: string): string {
    if (testResult === this.gostUtils.testResultDisp.good) {
      return "is-good";
    }
    if (testResult === this.gostUtils.testResultDisp.acceptable) {
      return "is-warn";
    }
    if (testResult === this.gostUtils.testResultDisp.bad) {
      return "is-bad";
    }
    return "is-neutral";
  }

  getScTrialResultLabel(columnDef: string): string {
    const tableDef = this.getActiveScTableDef();
    if (!tableDef || !columnDef || !tableDef.isMeasured(columnDef)) {
      return "-";
    }
    return this.getScResultDisplay(this.getScTestResult(tableDef, columnDef));
  }

  getScTrialResultClass(columnDef: string): string {
    const tableDef = this.getActiveScTableDef();
    if (!tableDef || !columnDef || !tableDef.isMeasured(columnDef)) {
      return "is-neutral";
    }
    return this.getScTestResultClassByValue(this.getScTestResult(tableDef, columnDef));
  }

  getScActiveTestResultLabel(): string {
    if (!this.isScMeasuredOnActiveTrial()) {
      return this.isInputMode() ? $localize`:@@home.gostInfos.scStatus.notEntered:未入力` : $localize`:@@home.gostInfos.scStatus.notMeasured:未測定`;
    }
    return this.getScResultDisplay(this.getScActiveTestResult());
  }

  getScActiveTestResultClass(): string {
    if (!this.isScMeasuredOnActiveTrial()) {
      return "is-neutral";
    }
    return this.getScTestResultClassByValue(this.getScActiveTestResult());
  }

  getScRowLabel(tableDef: ScTableDef, rowCode: string): string {
    const row = tableDef?.tableRows?.find((r) => r.code === rowCode);
    return row?.shortLabel || row?.label || rowCode;
  }

  getScRowFormalLabel(tableDef: ScTableDef, rowCode: string): string {
    const row = tableDef?.tableRows?.find((r) => r.code === rowCode);
    return row?.label || row?.shortLabel || rowCode;
  }

  isScAbnormalCode(rowCode: string): boolean {
    return this.scAbnormalCodes.includes(rowCode);
  }

  isScCellPositive(
    tableDef: ScTableDef,
    rowCode: string,
    columnDef: string,
  ): boolean {
    if (!tableDef || !columnDef) {
      return false;
    }
    const row = tableDef.tableRows.find((r) => r.code === rowCode);
    if (!row) {
      return false;
    }
    const colIndex = tableDef.dataColumnDefs.findIndex((c) => c === columnDef);
    if (colIndex < 0) {
      return false;
    }
    return !!row.cellValues[colIndex];
  }

  setScCellPositive(
    tableDef: ScTableDef,
    rowCode: string,
    columnDef: string,
    selected: boolean,
  ) {
    if (!tableDef || !columnDef) {
      return;
    }

    const round = tableDef.getScRoundByColumnDef(columnDef);
    const wasUnmeasured = !round.isMeasured;
    if (wasUnmeasured) {
      // カード入力では最初の操作で自動的に測定済みにする
      round.isMeasured = true;
    }

    // フィールドが未設定の場合、明示的にデフォルト値(TN)を設定する
    // （UI上「無」選択 と「未タッチ」を区別するため）
    if (round[rowCode] == null && !selected) {
      round[rowCode] = 'TN';
      tableDef.roundToCellsValuesColumn(columnDef);
      return;
    }

    const current = this.isScCellPositive(tableDef, rowCode, columnDef);
    if (current === selected) {
      if (wasUnmeasured) {
        tableDef.roundToCellsValuesColumn(columnDef);
      }
      return;
    }
    tableDef.toggleScCell(columnDef, rowCode);
  }

  clearScActiveTrial() {
    const tableDef = this.getActiveScTableDef();
    const columnDef = this.getScActiveColumnDef();
    if (!tableDef || !columnDef) {
      return;
    }

    const round = tableDef.getScRoundByColumnDef(columnDef);
    round.isMeasured = false;
    for (const code of this.scEditableCodes) {
      delete round[code];
    }
    tableDef.roundToCellsValuesColumn(columnDef);
  }

  goToNextUnfilledScTrial() {
    const tableDef = this.getActiveScTableDef();
    if (!tableDef?.dataColumnDefs?.length) {
      return;
    }

    const current = this.getScActiveTrialIndex();
    const order = [
      ...tableDef.dataColumnDefs.slice(current + 1),
      ...tableDef.dataColumnDefs.slice(0, current + 1),
    ];

    const nextUnmeasured = order.find((col) => !tableDef.isMeasured(col));
    if (nextUnmeasured) {
      const nextIndex = tableDef.dataColumnDefs.findIndex((col) => col === nextUnmeasured);
      this.selectScTrialByIndex(nextIndex);
      return;
    }

    if (current < tableDef.dataColumnDefs.length - 1) {
      this.selectScTrialByIndex(current + 1);
    }
  }

  /** 指定試行の異常チェック件数を返す */
  getScAbnormalCount(tableDef: ScTableDef, columnDef: string): number {
    if (!tableDef || !columnDef || !tableDef.isMeasured(columnDef)) {
      return 0;
    }
    return this.scAbnormalCodes.filter((code) =>
      this.isScCellPositive(tableDef, code, columnDef),
    ).length;
  }

  /** デスクトップインラインテーブルの異常展開トグル */
  toggleScInlineAbnormal(columnDef: string) {
    this.scExpandedAbnormalCol =
      this.scExpandedAbnormalCol === columnDef ? null : columnDef;
    // 展開した試行をアクティブにする
    const tableDef = this.getActiveScTableDef();
    if (tableDef) {
      const idx = tableDef.dataColumnDefs.indexOf(columnDef);
      if (idx >= 0) {
        this.selectScTrialByIndex(idx);
      }
    }
  }

  /** モバイル異常セクション展開トグル */
  toggleScMobileAbnormal() {
    this.scMobileAbnormalExpanded = !this.scMobileAbnormalExpanded;
  }

  /** デスクトップ行クリック: 試行選択 + 他行の注意項目パネルを閉じる */
  onScInlineRowClick(rowIdx: number, columnDef: string) {
    this.selectScTrialByIndex(rowIdx);
    // 自行以外の注意項目パネルが開いていたら閉じる（自行はtoggleで制御）
    if (this.scExpandedAbnormalCol && this.scExpandedAbnormalCol !== columnDef) {
      this.scExpandedAbnormalCol = null;
    }
  }

  /** ドキュメントクリック: SC注意項目パネルを外部クリックで閉じる */
  onDocumentClick(event: MouseEvent) {
    if (!this.scExpandedAbnormalCol && !this.scMobileAbnormalExpanded) {
      return;
    }
    const target = event.target as HTMLElement;
    if (!target) {
      return;
    }
    // SC インラインテーブルまたはモバイル異常セクション内のクリックは無視
    const scContainer = target.closest('.gost-sc-inline, .gost-sc-mobile__abnormal-toggle, .gost-sc-mobile__abnormal');
    if (!scContainer) {
      this.scExpandedAbnormalCol = null;
      // 入力モード中はモバイル注意項目をデフォルト展開のまま維持
      if (!this.isInputMode()) {
        this.scMobileAbnormalExpanded = false;
      }
      this.cdr.markForCheck();
    }
  }

  /** インラインテーブル行クリック時: 嚥下/むせを直接トグルする */
  onScInlineToggle(
    tableDef: ScTableDef,
    columnDef: string,
    rowCode: string,
    selected: boolean,
  ) {
    if (!this.isInputMode()) {
      return;
    }
    // 別の列を操作したら注意項目パネルを閉じる
    if (this.scExpandedAbnormalCol && this.scExpandedAbnormalCol !== columnDef) {
      this.scExpandedAbnormalCol = null;
    }
    this.setScCellPositive(tableDef, rowCode, columnDef, selected);
  }

  /** インラインテーブル行の異常トグル */
  onScInlineAbnormalToggle(tableDef: ScTableDef, columnDef: string, rowCode: string) {
    if (!this.isInputMode()) {
      return;
    }
    this.setScCellPositive(
      tableDef,
      rowCode,
      columnDef,
      !this.isScCellPositive(tableDef, rowCode, columnDef),
    );
  }

  /** 指定試行の嚥下の有無を返す */
  isScSwallowPresent(tableDef: ScTableDef, columnDef: string): boolean {
    return this.isScCellPositive(tableDef, "swallowResult", columnDef);
  }

  /** 指定試行のむせの有無を返す */
  isScCoughPresent(tableDef: ScTableDef, columnDef: string): boolean {
    return this.isScCellPositive(tableDef, "coughResult", columnDef);
  }

  /** 指定試行のフィールドが明示的に設定されているかを返す（未タッチと明示的false/TN を区別） */
  isScFieldExplicitlySet(tableDef: ScTableDef, rowCode: string, columnDef: string): boolean {
    if (!tableDef || !columnDef) {
      return false;
    }
    const round = tableDef.getScRoundByColumnDef(columnDef);
    return round?.[rowCode] != null;
  }

  getGcOcInputProgress(tableDef: GcOcTebleDef): number {
    const total = tableDef?.tableRows?.length ?? 0;
    if (total === 0) {
      return 0;
    }
    const filled = tableDef.getFilledRowCount();
    return Math.round((filled / total) * 100);
  }
}

export interface QuestionnaireAnswerHtmlReference {
  questionCode: string;
  references: ElementRef<HTMLInputElement>[];
}

class TableRow {
  code: string;
  label: string;
  shortLabel?: string;
  cellValues: string[];

  constructor(
    code: string,
    label: string,
    cellValues: string[],
    shortLabel?: string,
  ) {
    this.code = code;
    this.label = label;
    this.shortLabel = shortLabel;
    this.cellValues = cellValues;
  }
}

class TableDef {
  originalData;

  tableRows: TableRow[];
  rowlabels: string[];
  dataSource: MatTableDataSource<DescriptionsData>;

  dataColumnDefs: string[];
  allColumnDefs: string[];

  constructor(
    protected gostInfoClassInstance: GostInfosComponent,
    originalData,
    columns: string[],
    tableRows: TableRow[],
  ) {
    this.originalData = originalData;

    this.dataColumnDefs = columns;
    this.allColumnDefs = ["category", ...columns];

    this.tableRows = tableRows;

    this.dataSource = new MatTableDataSource<DescriptionsData>(this.tableRows);

    this.rowlabels = this.tableRows.map((g) => g.label);
  }

  setCellValue(rowCode: string, colName: string, newValue) {
    const row = this.tableRows.find((r) => r.code === rowCode);
    const colIndex = this.dataColumnDefs.findIndex((c) => c == colName);

    if (row !== null && colIndex !== -1) {
      row.cellValues[colIndex] = newValue;
    }
  }
}

class GcOcTebleDef extends TableDef {
  constructor(
    private gi: GostInfosComponent,
    originalData,
    dataColumns,
    tableRows: TableRow[],
  ) {
    super(gi, originalData, dataColumns, tableRows);

    this.initOriginalDataMetrics();
  }

  chipSelectionChange(event: MatChipSelectionChange, rowIndex, colIndex) {
    let rowCode = this.tableRows[rowIndex].code;
    const metric = this.getOrCreateMetric(rowCode);

    const newScore = colIndex - 3;

    if (event.selected) {
      metric.score = newScore;
    } else {
      if (metric.score == newScore) {
        metric.score = null;
      }
    }

    this.gostInfoClassInstance.gostUtils.evaluateGost(
      this.gostInfoClassInstance.recordData,
    );
    this.gostInfoClassInstance.updateMobileSummaries();
  }

  setScoreByRowIndex(rowIndex: number, colIndex: number) {
    const rowCode = this.tableRows[rowIndex].code;
    const metric = this.getOrCreateMetric(rowCode);
    metric.score = colIndex - 3;

    this.gostInfoClassInstance.gostUtils.evaluateGost(
      this.gostInfoClassInstance.recordData,
    );
    this.gostInfoClassInstance.updateMobileSummaries();
  }

  setScoreByValue(rowCode: string, score: number) {
    const metric = this.getOrCreateMetric(rowCode);
    metric.score = score;

    this.gostInfoClassInstance.gostUtils.evaluateGost(
      this.gostInfoClassInstance.recordData,
    );
    this.gostInfoClassInstance.updateMobileSummaries();
  }

  getScore(rowCode: string): number | null {
    const score = this.getOrCreateMetric(rowCode).score;
    return score == null ? null : score;
  }

  getSelectedOptionLabel(rowCode: string): string {
    const row = this.tableRows.find((r) => r.code === rowCode);
    if (!row) {
      return "未選択";
    }

    const score = this.getScore(rowCode);
    if (score == null) {
      return "未選択";
    }

    const optionIndex = score + 3;
    return row.cellValues[optionIndex] ?? "未選択";
  }

  getFilledRowCount(): number {
    return this.tableRows.filter((r) => this.getScore(r.code) != null).length;
  }

  initOriginalDataMetrics() {

    for (var i = 0; i < this.tableRows.length; i++) {
      const code = this.tableRows[i].code;
      let metric = this.getOrCreateMetric(code);
    }

  }

  getOrCreateMetric(code) {
    let metric = this.originalData.metrics.find((m) => m.code == code);

    if (!metric) {
      metric = { code: code, score: null };
      this.originalData.metrics.push(metric);
    }

    return metric;
  }
}

class ScTableDef extends TableDef {
  detailHidden = true;
  level;

  constructor(
    private gi: GostInfosComponent,
    originalData,
    dataColumns,
    private withOrWithoutThickness,
    tableRows: TableRow[],
  ) {
    super(gi, originalData, dataColumns, tableRows);

    this.initOriginalDataMetrics();
  }

  initOriginalDataMetrics() {
    for (var dataColumnDef of this.dataColumnDefs) {
      this.roundToCellsValuesColumn(dataColumnDef);
    }
  }

  roundToCellsValuesColumn(colName: string) {
    let [amount, roundNum] = this.colName2AmountAndRound(colName);

    const round = this.getScRound(amount, roundNum);

    const convertedRound =
      this.gostInfoClassInstance.gostUtils.evaluateScRound(round);

    // todo TP, TN, FP, FNとスコアとの関連を確認
    // todo 特に challengeのスコアの計算法を確認（項目が違うので）
    this.setCellValue(
      "swallowResult",
      colName,
      this.scResultToCellvalue(convertedRound["swallowResult"]),
    );

    this.setCellValue(
      "coughResult",
      colName,
      this.scResultToCellvalue(convertedRound["coughResult"]),
    );

    this.setCellValue(
      "swallowDelay",
      colName,
      this.scResultToCellvalue(convertedRound["swallowDelay"]),
    );
    this.setCellValue(
      "abnormalSound",
      colName,
      this.scResultToCellvalue(convertedRound["abnormalSound"]),
    );
    this.setCellValue(
      "multiSwallows",
      colName,
      this.scResultToCellvalue(convertedRound["multiSwallows"]),
    );
    this.setCellValue(
      "throatClear",
      colName,
      this.scResultToCellvalue(convertedRound["throatClear"]),
    );
    this.setCellValue(
      "otherAbbration",
      colName,
      this.scResultToCellvalue(convertedRound["otherAbbration"]),
    );

    this.setCellValue(
      "otherAbnormality",
      colName,
      this.scResultToCellvalue(convertedRound["otherAbnormality"]),
    );

    this.setCellValue("testResult", colName, convertedRound["testResult"]);

    this.gostInfoClassInstance.gostUtils.evaluateGost(
      this.gostInfoClassInstance.recordData,
    );
    this.gostInfoClassInstance.updateMobileSummaries();
  }

  toggleMeasured(event: any, columnDef?: string) {
    // チェックOFF時: そのトライアルの入力データをクリア（カードUIの「入力をクリア」と同じ挙動）
    if (columnDef) {
      const round = this.getScRoundByColumnDef(columnDef);
      if (!round.isMeasured) {
        for (const code of this.gostInfoClassInstance.scEditableCodes) {
          delete round[code];
        }
        this.roundToCellsValuesColumn(columnDef);
        return;
      }
    }
    this.gostInfoClassInstance.gostUtils.evaluateGost(
      this.gostInfoClassInstance.recordData,
    );
    this.gostInfoClassInstance.updateMobileSummaries();
  }

  scResultToCellvalue(scResult: string): boolean {
    if (scResult == "TP" || scResult == "FN") {
      return true;
    }
    // else if (scResult == 'TN' || scResult == 'FP'){
    // 	return 'remove';
    // }
    else {
      return false;
    }
  }

  getScRoundByColumnDef(colName: string) {
    let [amount, roundNum] = this.colName2AmountAndRound(colName);

    return this.getScRound(amount, roundNum);
  }

  isMeasured(colName: string) {
    return this.getScRoundByColumnDef(colName).isMeasured;
  }

  getScRound(amount: number, roundNum: number) {
    const gostSCData = this.originalData;
    const steps = gostSCData[this.withOrWithoutThickness];

    var step = steps.find((s) => s.amount == amount);
    if (!step) {
      step = { amount: amount, rounds: [] };
      steps.push(step);
    }

    var round = step.rounds.find((s) => s.round == roundNum);
    if (!round) {
      round = { round: roundNum, isMeasured: false };
      step.rounds.push(round);
    }

    return round;
  }

  toggleScCell(colName: string, code: string) {
    let [amount, roundNum] = this.colName2AmountAndRound(colName);

    const round = this.getScRound(amount, roundNum);

    var newValue;
    if (round[code] == "TP" || round[code] == "FN") {
      newValue = "TN";
    } else {
      newValue = "TP";
    }

    round[code] = newValue;

    this.roundToCellsValuesColumn(colName);
  }

  colName2AmountAndRound(colName): [number, number] {
    // colNameには "1ml 1回目" とか "30ml" とか入っているので、これをパースして amount と round を取得する
    var amount, roundNum;
    const match = colName.match(/(\d+)ml(?: (\d+))?/);
    if (match) {
      amount = parseInt(match[1]);
      roundNum = parseInt(match[2]) || 1;
    }

    return [amount, roundNum];
  }

  toggleDetails() {
    this.detailHidden = !this.detailHidden;
    this.gostInfoClassInstance.scrollToSection("sc");
  }
}

interface TableColumn {
  columnDef: string;
  header: string;
}

interface TableData {
  category: string;
  [key: string]: string | number | boolean; // これで各行のデータを動的に保持できます
}

interface DescriptionsData {
  code: string;
  label: string;
  cellValues: any[];
  // [key: string]: string | number | boolean; // これで各行のデータを動的に保持できます
}
